import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/data';

const articleImages: Record<string, string> = {
  'kaiyun-download-latest-version-guide': 'https://readdy.ai/api/search-image?query=Software%20download%20and%20installation%20guide%20concept%20with%20modern%20computer%20screen%20showing%20download%20progress%2C%20warm%20orange%20accents%2C%20clean%20minimalist%20tech%20illustration%20on%20light%20background&width=1200&height=600&seq=art-detail-001&orientation=landscape',
  'kaiyun-app-mobile-download-guide': 'https://readdy.ai/api/search-image?query=Smartphone%20app%20download%20concept%20with%20mobile%20device%20showing%20app%20store%20interface%2C%20warm%20orange%20and%20teal%20accents%2C%20clean%20minimalist%20illustration%20on%20light%20background&width=1200&height=600&seq=art-detail-002&orientation=landscape',
  'kaiyun-download-speed-optimization': 'https://readdy.ai/api/search-image?query=High%20speed%20internet%20optimization%20concept%20with%20abstract%20speed%20lines%20and%20network%20visualization%2C%20warm%20orange%20accents%2C%20clean%20modern%20tech%20illustration&width=1200&height=600&seq=art-detail-003&orientation=landscape',
  'kaiyun-security-scan-feature': 'https://readdy.ai/api/search-image?query=Digital%20security%20shield%20with%20virus%20scan%20visualization%2C%20abstract%20cybersecurity%20concept%2C%20warm%20orange%20accents%20on%20light%20background%2C%20minimalist%20illustration&width=1200&height=600&seq=art-detail-004&orientation=landscape',
  'kaiyun-new-features-2026': 'https://readdy.ai/api/search-image?query=AI%20artificial%20intelligence%20and%20cloud%20technology%20concept%2C%20modern%20digital%20brain%20with%20network%20connections%2C%20warm%20orange%20accents%2C%20clean%20minimalist%20illustration&width=1200&height=600&seq=art-detail-005&orientation=landscape',
  'kaiyun-vs-other-downloaders': 'https://readdy.ai/api/search-image?query=Software%20comparison%20analysis%20concept%20with%20abstract%20charts%20and%20data%20visualization%2C%20warm%20orange%20accents%2C%20modern%20minimalist%20tech%20illustration%20on%20light%20background&width=1200&height=600&seq=art-detail-006&orientation=landscape',
};

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-600 mb-4">文章未找到</p>
            <Link to="/" className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-24 pb-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '文章', path: '/category/system-tools' },
              { label: article.title },
            ]}
          />

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读 {article.readTime}
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="aspect-[2/1] rounded-xl overflow-hidden mb-10 bg-background-100">
            <img
              src={articleImages[article.slug]}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div
            className="prose prose-lg max-w-none mb-12
              prose-headings:text-foreground-900 prose-headings:font-heading
              prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed
              prose-li:text-foreground-700 prose-li:leading-relaxed
              prose-strong:text-foreground-900
              prose-ul:list-disc prose-ol:list-decimal
              [&_ul]:pl-5 [&_ol]:pl-5
              [&_li]:mb-2"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Share */}
          <div className="flex items-center gap-3 py-6 border-t border-background-200/70 mb-12">
            <span className="text-sm text-foreground-500">分享到：</span>
            <a href="/" className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微博" rel="nofollow">
              <i className="ri-weibo-line"></i>
            </a>
            <a href="/" className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微信" rel="nofollow">
              <i className="ri-wechat-line"></i>
            </a>
            <a href="/" className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接" rel="nofollow">
              <i className="ri-link"></i>
            </a>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="bg-background-50 rounded-xl border border-background-200/70 p-5 hover:border-primary-300 transition-all duration-200 group cursor-pointer"
                  >
                    <h3 className="font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors text-sm">
                      {ra.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>{ra.date}</span>
                      <span>{ra.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Schema.org */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              author: { '@type': 'Person', name: article.author },
              datePublished: article.date,
              image: articleImages[article.slug],
              publisher: {
                '@type': 'Organization',
                name: '开云下载',
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyunxiazai.com/' },
                { '@type': 'ListItem', position: 2, name: '文章', item: 'https://www.kaiyunxiazai.com/category/system-tools' },
                { '@type': 'ListItem', position: 3, name: article.title },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}